/**
 * OCT20 WS-ROLL (FD-11) + OCT20A WS-ROLL2 (R3-A / R3-B / R4) — today's Dayboard strip for scheduling questions the
 * server is holding open.
 *
 *   "Scheduling Decision Required"  work whose day already ended unresolved (a multiday repair's last day, an approved
 *                                   continuation, or a single-day repair). The Dayboard reads one date, so without this
 *                                   list it would vanish (Floyd's, TKT-0018). Includes a decision a person recorded but
 *                                   has not carried out yet — recording is not resolving.
 *   "Projected EOD Overrun — N Minutes"  a technician's committed lane now finishes past their own end of day, with
 *                                   every affected ticket (also jobs a cascade pushed later) and its own overtime.
 *
 * Both lists sit BESIDE the lanes, never inside one: nothing is booked, so nothing consumes capacity. Approvers decide
 * in the existing end-of-day decision panel ("Open end-of-day decisions" expands it); nothing is decided here. The
 * only write is "Acknowledge" (acknowledge_schedule_alert), which the server records and which never stops reminders.
 * The source of truth is the server (schedule_alert_conflicts — evaluated at commit and every minute by pg_cron).
 */
import { EodOverrunActions } from '@/components/scheduling/EodOverrunActions';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useScheduleSignalRefresh } from '@/hooks/useScheduleSignalRefresh';
import { useLatestResponseGuard } from '@/hooks/useLatestResponseGuard';
import {
  buildDecisionRequiredRows, describeDecisionRequiredRow, DECISION_REQUIRED_LABEL, ticketLabel,
  type DecisionConversionRow, type DecisionRequiredRow, type DecisionTicketRow,
} from '@/lib/rollover/decisionRequired';
import {
  buildEodOverrunRows, buildSchedulingDecisionAlertRows, describeRecordedDecision,
  type AlertConflictRow, type AlertTicketRow, type EodMemberFacts, type EodOverrunStripRow, type SchedulingDecisionAlertRow,
} from '@/lib/rollover/scheduleAlerts';
import { OPEN_ROLLOVER_DECISIONS_EVENT, type OpenRolloverDecisionsDetail } from '@/components/scheduling/RolloverDecisionPanel';

type LooseDb = { from: (t: string) => any; rpc: (f: string, a: Record<string, unknown>) => any };

interface Props {
  shopId: string | null | undefined;
  /** The shop's local today (YYYY-MM-DD). */
  today: string;
  /** Only the current day's board shows the strip. */
  isToday: boolean;
  /** The shop's time zone (for reminder times). */
  timeZone?: string;
}

interface StaleRow extends DecisionRequiredRow { alert?: SchedulingDecisionAlertRow }

export function RolloverDecisionRequiredStrip({ shopId, today, isToday, timeZone }: Props) {
  const db = supabase as unknown as LooseDb;
  const [stale, setStale] = useState<StaleRow[]>([]);
  const [eod, setEod] = useState<EodOverrunStripRow[]>([]);
  const [mayDecide, setMayDecide] = useState(false);
  // OCT28 #9: the server's answer for THIS person (owner / manager / org admin); the server still decides every write.
  const [mayOvertime, setMayOvertime] = useState(false);
  const { begin, invalidate } = useLatestResponseGuard();   // Issue #26: an older answer never replaces a newer one
  const [acked, setAcked] = useState<Record<string, string>>({});

  const formatTime = useMemo(() => {
    const f = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', ...(timeZone ? { timeZone } : {}) });
    return (iso: string) => { const d = new Date(iso); return Number.isFinite(d.getTime()) ? f.format(d) : iso; };
  }, [timeZone]);
  // OCT28 #9: a due-out or a multi-day finish can be on another day — say which.
  const formatWhen = useMemo(() => {
    const f = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', ...(timeZone ? { timeZone } : {}) });
    return (iso: string) => { const d = new Date(iso); return Number.isFinite(d.getTime()) ? f.format(d) : iso; };
  }, [timeZone]);

  const load = useCallback(async () => {
    if (!shopId || !isToday) return;
    const isLatest = begin();
    try {
      const [{ data: convs }, { data: alerts }] = await Promise.all([
        db.from('rollover_conversions')
          .select('id, origin_ticket_id, origin_date, kind, detection, acknowledged_at')
          .eq('shop_id', shopId).eq('state', 'proposed').lt('origin_date', today)
          .order('origin_date', { ascending: true }).limit(50),
        db.from('schedule_alert_conflicts')
          .select('id, kind, technician_id, work_date, state, max_overrun_min, last_notified_at, next_reminder_at, notification_count, suppressed_since, acknowledged_at, summary')
          .eq('shop_id', shopId).eq('state', 'open').order('work_date', { ascending: true }).limit(100),
      ]);
      const conflicts = (alerts ?? []) as AlertConflictRow[];
      const decisionAlerts = buildSchedulingDecisionAlertRows(conflicts, today, formatTime);
      const list = (convs ?? []) as DecisionConversionRow[];
      const eodIds = conflicts.filter((c) => c.kind === 'eod_overrun').map((c) => c.id);
      const { data: alertTickets } = eodIds.length
        ? await db.from('schedule_alert_conflict_tickets')
          .select('conflict_id, ticket_id, active, overrun_min, projected_finish, pushed_by_cascade, conversion_id, boundary_at')
          .in('conflict_id', eodIds).eq('active', true)
        : { data: [] };
      const ticketIds = [...new Set([
        ...list.map((c) => c.origin_ticket_id),
        ...decisionAlerts.map((a) => a.ticketId),
        ...((alertTickets ?? []) as AlertTicketRow[]).map((t) => t.ticket_id),
      ])];
      const techIds = [...new Set(conflicts.map((c) => c.technician_id).filter(Boolean))] as string[];
      const [{ data: tickets }, { data: techs }] = await Promise.all([
        ticketIds.length
          ? db.from('tickets').select('id, simple_ticket_number, customer_name, status, scheduled_date, deleted_at, actually_started, due_out, rollover_of_ticket_id, rollover_conversion_id').in('id', ticketIds)
          : Promise.resolve({ data: [] }),
        techIds.length ? db.from('technicians').select('id, name').in('id', techIds) : Promise.resolve({ data: [] }),
      ]);
      const tk = (tickets ?? []) as DecisionTicketRow[];
      const rows: StaleRow[] = buildDecisionRequiredRows(list, tk, today);
      const byKey = new Map(rows.map((r) => [`${r.ticketId}:${r.lastApprovedDate}`, r]));
      // a recorded-but-unexecuted decision (no open proposal) stays listed through its open alert
      for (const a of decisionAlerts) {
        const k = `${a.ticketId}:${a.workDate}`;
        const hit = byKey.get(k);
        if (hit) { hit.alert = a; continue; }
        const t = tk.find((x) => x.id === a.ticketId);
        if (!t) continue;
        const row: StaleRow = {
          conversionId: a.conflictId, ticketId: a.ticketId, ticketLabel: ticketLabel(t), customerName: t.customer_name ?? null,
          lastApprovedDate: a.workDate,
          daysOverdue: Math.max(0, Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${a.workDate}T00:00:00Z`)) / 86_400_000)),
          started: true, acknowledged: false, source: a.source, alert: a,
        };
        rows.push(row); byKey.set(k, row);
      }
      rows.sort((a, b) => a.lastApprovedDate.localeCompare(b.lastApprovedDate) || a.ticketId.localeCompare(b.ticketId));
      if (!isLatest()) return;   // Issue #26: a newer read has begun — its answer decides what is shown
      setStale(rows);
      const names = Object.fromEntries(((techs ?? []) as Array<{ id: string; name: string }>).map((t) => [t.id, t.name]));
      // OCT28 #9: which overrun tickets head an approved multi-day plan (a live continuation exists) — named on the row.
      const overrunIds = [...new Set(((alertTickets ?? []) as AlertTicketRow[]).map((t) => t.ticket_id))];
      const { data: conts } = overrunIds.length
        ? await db.from('tickets').select('rollover_of_ticket_id, scheduled_date, opti_finish').in('rollover_of_ticket_id', overrunIds)
          .is('deleted_at', null).in('status', ['Active', 'Paused'])
        : { data: [] };
      const continuations: Record<string, string> = {};
      const repairFinish: Record<string, string> = {};   // OCT28 #9: when the whole repair now finishes (latest live day)
      for (const c of (conts ?? []) as Array<{ rollover_of_ticket_id: string; scheduled_date: string; opti_finish?: string | null }>) {
        const d = String(c.scheduled_date ?? '').slice(0, 10);
        if (d && (!continuations[c.rollover_of_ticket_id] || d < continuations[c.rollover_of_ticket_id])) continuations[c.rollover_of_ticket_id] = d;
        if (c.opti_finish && (!repairFinish[c.rollover_of_ticket_id] || Date.parse(c.opti_finish) > Date.parse(repairFinish[c.rollover_of_ticket_id]))) {
          repairFinish[c.rollover_of_ticket_id] = c.opti_finish;
        }
      }
      // OCT28 #9: which case each member is (started? itself a later day of a plan?) and the customer's promise.
      const facts: Record<string, EodMemberFacts> = {};
      for (const t of tk as Array<DecisionTicketRow & { actually_started?: boolean | null; due_out?: string | null;
        rollover_of_ticket_id?: string | null; rollover_conversion_id?: string | null }>) {
        facts[t.id] = { started: t.actually_started === true, isContinuation: !!(t.rollover_of_ticket_id || t.rollover_conversion_id),
          dueOut: t.due_out ?? null, repairFinish: repairFinish[t.id] ?? null };
      }
      if (!isLatest()) return;
      setEod(buildEodOverrunRows(conflicts, (alertTickets ?? []) as AlertTicketRow[],
        tk.map((t) => ({ id: t.id, simple_ticket_number: t.simple_ticket_number, customer_name: t.customer_name })), names, today, formatTime,
        continuations, facts, formatWhen));
    } catch {
      // a failed READ keeps the last list; nothing is written
    }
  }, [db, shopId, today, isToday, formatTime, formatWhen, begin]);

  useEffect(() => {
    if (!shopId || !isToday) { invalidate(); setStale([]); setEod([]); return; }
    void load();
    const t = setInterval(() => { void load(); }, 60_000);
    return () => clearInterval(t);
  }, [shopId, isToday, load, invalidate]);
  useScheduleSignalRefresh(() => { void load(); }, { enabled: !!shopId && isToday });
  // R3-A Option B: the server reconciles with no board open and records / updates / resolves the end-of-day
  // conflict AFTER it walked the lanes — follow the conflict itself (20270209030000 publishes it; RLS scopes it to
  // this shop's members). Its OWN channel: if the publication is missing on a target, only this channel fails and
  // the 60 s poll above remains the backstop (an unpublished table silences every binding of a shared channel).
  useEffect(() => {
    if (!shopId || !isToday) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const channel = supabase
      .channel(`schedule-alert-conflicts-${shopId}`)
      .on('postgres_changes' as never, {
        event: '*', schema: 'public', table: 'schedule_alert_conflicts', filter: `shop_id=eq.${shopId}`,
      } as never, () => {
        if (timer) return;
        timer = setTimeout(() => { timer = null; void load(); }, 250);
      })
      .subscribe();
    return () => {
      if (timer) clearTimeout(timer);
      void supabase.removeChannel(channel);
    };
  }, [shopId, isToday, load]);

  useEffect(() => {
    if (!shopId) return;
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await db.rpc('can_i_decide_rollover', { p_shop: shopId });
        if (!cancelled) setMayDecide(data === true);
      } catch { if (!cancelled) setMayDecide(false); }
      try {
        const { data, error } = await db.rpc('can_i_authorize_overtime', { p_shop: shopId });
        if (!cancelled) setMayOvertime(!error && data === true);
      } catch { if (!cancelled) setMayOvertime(false); }
    })();
    return () => { cancelled = true; };
  }, [db, shopId]);

  // OCT28 #9: the panel is told which overruns have no decision record, so it never contradicts this strip.
  const openDecisions = () => {
    const detail: OpenRolloverDecisionsDetail = {
      withoutDecision: eod.flatMap((c) => c.tickets.filter((t) => !t.awaitingDecision && t.overrunMin > 0)
        .map((t) => ({ ticketId: t.ticketId, label: t.label, text: t.text }))),
    };
    window.dispatchEvent(new CustomEvent(OPEN_ROLLOVER_DECISIONS_EVENT, { detail }));
  };
  const acknowledge = async (conflictId: string) => {
    try {
      const { error } = await db.rpc('acknowledge_schedule_alert', { p_conflict: conflictId });
      setAcked((a) => ({ ...a, [conflictId]: error ? 'Could not record the acknowledgement.' : 'Acknowledged — reminders continue until it is resolved.' }));
    } catch {
      setAcked((a) => ({ ...a, [conflictId]: 'Could not record the acknowledgement.' }));
    }
  };

  if (!isToday || (stale.length === 0 && eod.length === 0)) return null;
  return (
    <div className="mx-2 my-2 space-y-2">
      {stale.length > 0 && (
        <section role="region" aria-label={DECISION_REQUIRED_LABEL} data-testid="rollover-decision-required"
          className="rounded-lg border-2 border-dashed border-amber-500 bg-amber-50 px-3 py-2 text-amber-950 dark:bg-amber-950 dark:text-amber-50">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-sm font-semibold">{DECISION_REQUIRED_LABEL} ({stale.length})</h2>
            <span className="text-xs opacity-80">Shown for a decision only — no time is reserved on today’s schedule.</span>
          </div>
          <ul className="mt-1 space-y-1">
            {stale.map((r) => {
              const recorded = describeRecordedDecision(r.alert?.recordedDecision ?? null, r.lastApprovedDate);
              return (
                <li key={r.conversionId} className="text-sm" data-testid="rollover-decision-required-row">
                  <div className="flex flex-wrap gap-x-2">
                    <span className="font-medium">{r.ticketLabel}</span>
                    {r.customerName ? <span>· {r.customerName}</span> : null}
                    <span className="opacity-90">· {describeDecisionRequiredRow(r)}</span>
                    {r.acknowledged ? <span className="text-xs opacity-75">(left unresolved)</span> : null}
                  </div>
                  {recorded ? <div className="text-xs" data-testid="recorded-decision">{recorded}</div> : null}
                  {r.alert?.reminder ? <div className="text-xs opacity-75">{r.alert.reminder}</div> : null}
                  {r.alert ? (
                    <button type="button" className="mt-1 min-h-[44px] text-xs underline" onClick={() => void acknowledge(r.alert!.conflictId)}>
                      Acknowledge
                    </button>
                  ) : null}
                  {r.alert && acked[r.alert.conflictId] ? <div className="text-xs" role="status">{acked[r.alert.conflictId]}</div> : null}
                </li>
              );
            })}
          </ul>
          {mayDecide ? (
            <button type="button" className="mt-1 min-h-[44px] rounded border border-amber-600 px-3 text-xs font-medium" onClick={openDecisions}
              data-testid="open-eod-decisions">
              Open end-of-day decisions
            </button>
          ) : null}
        </section>
      )}
      {eod.length > 0 && (
        <section role="region" aria-label="Projected end-of-day overruns" data-testid="eod-overrun-strip"
          className="rounded-lg border-2 border-red-500 bg-red-50 px-3 py-2 text-red-950 dark:bg-red-950 dark:text-red-50">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-sm font-semibold">Projected end-of-day overruns ({eod.length})</h2>
            <span className="text-xs opacity-80">Nothing is extended, authorized or carried until a person decides.</span>
          </div>
          <ul className="mt-1 space-y-2">
            {eod.map((c) => (
              <li key={c.conflictId} data-testid="eod-overrun-row" className="text-sm">
                <div className="font-medium">{c.technicianName} · {c.workDate === today ? 'today' : c.workDate} · {c.heading}</div>
                <ul className="ml-3 list-disc">
                  {c.tickets.map((t) => (
                    <li key={t.ticketId} data-testid="eod-overrun-ticket">
                      {t.label} — {t.text}
                      {t.dueOut ? (
                        <div data-testid={`eod-dueout-${t.ticketId}`} data-at-risk={t.dueOut.atRisk ? 'true' : 'false'}
                          className={t.dueOut.atRisk ? 'mt-0.5 text-xs font-semibold' : 'mt-0.5 text-xs opacity-80'}>
                          {t.dueOut.text}
                        </div>
                      ) : null}
                      {!t.awaitingDecision && t.overrunMin > 0 && (
                        <EodOverrunActions ticketId={t.ticketId} workDate={c.workDate} boundaryAt={t.boundaryAt ?? null}
                          projectedFinish={t.projectedFinish ?? null} overrunMin={t.overrunMin} mayAuthorizeOvertime={mayOvertime}
                          overtimeVerb={t.shape === 'plan_head' ? 'Extend today: authorize' : undefined} />
                      )}
                      {t.awaitingDecision && (
                        <div className="mt-1 flex flex-wrap items-center gap-2" data-testid={`eod-awaiting-actions-${t.ticketId}`}>
                          {mayDecide ? (
                            <button type="button" className="min-h-[44px] rounded border border-red-600 px-3 text-xs font-medium"
                              onClick={openDecisions} data-testid={`eod-review-decision-${t.ticketId}`}>
                              Review its end-of-day decision
                            </button>
                          ) : (
                            <span className="text-xs" data-testid={`eod-awaiting-who-${t.ticketId}`}>
                              Waiting for a manager or service advisor who decides end-of-day work.
                            </span>
                          )}
                          <a className="min-h-[44px] inline-flex items-center rounded border border-red-600 px-3 text-xs font-medium"
                             href={`/?tab=dayboard&highlight=${encodeURIComponent(t.ticketId)}&openDetails=true`}
                             data-testid={`eod-open-ticket-${t.ticketId}`}>
                            Open ticket
                          </a>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
                {c.reminder ? <div className="text-xs opacity-75">{c.reminder}</div> : null}
                <button type="button" className="mt-1 min-h-[44px] text-xs underline" onClick={() => void acknowledge(c.conflictId)}>
                  Acknowledge
                </button>
                {acked[c.conflictId] ? <div className="text-xs" role="status">{acked[c.conflictId]}</div> : null}
              </li>
            ))}
          </ul>
          {mayDecide ? (
            <button type="button" className="mt-1 min-h-[44px] rounded border border-red-600 px-3 text-xs font-medium" onClick={openDecisions}
              data-testid="open-eod-decisions-overrun">
              Open end-of-day decisions (reassign, overtime, rollover, reschedule)
            </button>
          ) : null}
        </section>
      )}
    </div>
  );
}
